import React from "react";
import { ChipList } from "../ChipList";
import { addChipValue, removeChipValue } from "../ValueMultiSelect";
import { PLACEHOLDER_COLUMNS, TRIP_DATA_FIELDS } from "../../reportConfigurationLogic";
import type { FilterGroupKey, ReportConfig, ReportConfigurationLookupOption, TripDataFieldDef, TripDataFieldState } from "../../types";
import { IconAction } from "../shared/EditorControls";

type TripDataFiltersEditorProps = {
    report: ReportConfig;
    editing: {
        replaceSelectedReport: (updater: (report: ReportConfig) => ReportConfig) => void;
    };
    catalog: {
        lookupOptionsForColumn: (columnKey: string, modelKey?: string | undefined) => ReportConfigurationLookupOption[];
    };
    session: {
        tripDataSearch: Record<string, string>;
        setTripDataSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    };
};

export function TripDataFiltersEditor({ report, editing, catalog, session }: TripDataFiltersEditorProps) {
    const { replaceSelectedReport } = editing;
    const { lookupOptionsForColumn } = catalog;
    const { tripDataSearch, setTripDataSearch } = session;

    function renderTripDataTab(report: ReportConfig) {
        if (!report.models.includes("transportation")) return <section className="rc-block"><h3>Trip Data & Exclusions</h3><p className="rc-hint">Attach the Transportation data model in Setup first - these fields are all Transportation columns.</p></section>;
        return <>{renderTripDataGroup(report, "include", "Only Include Rows Where...", "Leave every field blank to include everything. Once you set values here, only matching rows show up in every metric on this report that uses Transportation data.")}{renderTripDataGroup(report, "exclude", "Leave Out Rows Where...", "Rows matching this are removed from every metric on this report that uses Transportation data, on top of whatever the include rule above already narrowed down.")}</>;
    }

    function renderTripDataGroup(report: ReportConfig, groupKey: FilterGroupKey, title: string, hint: string) {
        const group = report.tripDataFilters[groupKey];
        return (
            <section className="rc-block rc-trip-section">
                <h3>{title}</h3>
                <p className="rc-block-hint">{hint}</p>
                <div className="rc-trip-logic">
                    <span>When more than one field below is set, require</span>
                    <select value={group.logic} onChange={(event) => replaceSelectedReport((current) => ({ ...current, tripDataFilters: { ...current.tripDataFilters, [groupKey]: { ...current.tripDataFilters[groupKey], logic: event.target.value as "and" | "or" } } }))}>
                        <option value="and">All of Them to Match (AND)</option>
                        <option value="or">Any of Them to Match (OR)</option>
                    </select>
                </div>
                <div className="rc-trip-grid">
                    {TRIP_DATA_FIELDS.map((field) => renderTripDataField(report, groupKey, field))}
                </div>
            </section>
        );
    }

    function renderTripDataField(report: ReportConfig, groupKey: FilterGroupKey, field: TripDataFieldDef) {
        const state = report.tripDataFilters[groupKey].fields[field.key] || { values: [], freeText: "", contains: "" };
        const options = lookupOptionsForColumn(field.column, "transportation");
        const searchKey = `${groupKey}:${field.key}`;
        const query = tripDataSearch[searchKey] || "";
        const normalizedQuery = query.trim().toLowerCase();
        const filteredOptions = normalizedQuery
            ? options.filter((option) => option.name.toLowerCase().includes(normalizedQuery) || option.value.toLowerCase().includes(normalizedQuery))
            : options;
        const labelForValue = (value: string) => options.find((option) => option.value === value)?.name || value;
        const updateValues = (values: string[]) => updateTripDataField(groupKey, field.key, (current) => ({ ...current, values }));

        return (
            <div className="rc-trip-field" key={`${groupKey}-${field.key}`}>
                <div className="rc-trip-field-head">
                    <div>
                        <div className="rc-lbl">{field.label}</div>
                        <p className="rc-hint">{state.values.length ? `${state.values.length} selected` : "No values selected"}</p>
                    </div>
                    {state.values.length > 0 && <IconAction label="Clear Selected Values" icon="backspace" onClick={() => updateValues([])} />}
                </div>
                <div className={field.hasText ? "rc-trip-field-body" : "rc-trip-field-body single"}>
                    <div className="rc-trip-values">
                        <div className="rc-trip-selected">
                            <ChipList values={state.values} onRemove={(value) => updateValues(removeChipValue(state.values, value))} emptyText="No values selected" getLabel={labelForValue} />
                        </div>
                        <input
                            className="rc-trip-search"
                            value={query}
                            onChange={(event) => setTripDataSearch((current) => ({ ...current, [searchKey]: event.target.value }))}
                            placeholder={`Search ${field.label.toLowerCase()} Values...`}
                        />
                        <div className="rc-trip-option-list">
                            {filteredOptions.length ? filteredOptions.map((option) => {
                                const checked = state.values.includes(option.value);
                                return (
                                    <label className={checked ? "rc-trip-option selected" : "rc-trip-option"} key={option.id} title={option.name}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(event) => updateValues(event.target.checked ? addChipValue(state.values, option.value) : removeChipValue(state.values, option.value))}
                                        />
                                        <span>{option.name}</span>
                                    </label>
                                );
                            }) : <div className="rc-trip-empty">{options.length ? "No Matching Values" : "No Lookup Values Loaded"}</div>}
                        </div>
                    </div>
                    {field.hasText && (
                        <div className="rc-trip-text-filters">
                            <label>
                                <span>Also Match This Exact Value</span>
                                <input placeholder="Type An Exact Value" value={state.freeText} onChange={(event) => updateTripDataField(groupKey, field.key, (current) => ({ ...current, freeText: event.target.value }))} />
                            </label>
                            <label>
                                <span>Also Match Anything Containing</span>
                                <input placeholder="e.g. Test" value={state.contains} onChange={(event) => updateTripDataField(groupKey, field.key, (current) => ({ ...current, contains: event.target.value }))} />
                            </label>
                        </div>
                    )}
                </div>
                {PLACEHOLDER_COLUMNS.has(field.column) && <p className="rc-src">Values are loaded from reporting lookups for {field.label.toLowerCase()}.</p>}
            </div>
        );
    }

    function updateTripDataField(groupKey: FilterGroupKey, fieldKey: string, updater: (field: TripDataFieldState) => TripDataFieldState) {
        replaceSelectedReport((report) => {
            const currentField = report.tripDataFilters[groupKey].fields[fieldKey] || { values: [], freeText: "", contains: "" };
            return {
        ...report,
        tripDataFilters: { ...report.tripDataFilters,
        [groupKey]: { ...report.tripDataFilters[groupKey],
        fields: { ...report.tripDataFilters[groupKey].fields,
        [fieldKey]: updater(currentField) } } },
    };
        });
    }
    return renderTripDataTab(report);
}
